import { BasicProfileData, Role } from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  LoadingStates,
  SectionError,
  SectionLoader,
  careerStateSelector,
  userProfileStateSelector,
} from '@careerhub/common';
import { Subtitle2 } from '@fluentui/react-components';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { CardsCarousel } from '../CardsCarouselV2/CardsCarouselV2';
import { SkeletonCards } from '../Shared/SkeletonCards/SkeletonCards';
import { EventName, FeatureName, SubFeatureName } from '../resources/TelemetryEventDetails';
import { useCareerPathStyles } from './CareerPath.styles';
import { ErrorContent } from '../resources/resources';
import { useSelector } from 'react-redux';
import { FeatureFlights } from '../../utilities/FeatureFlights';

export const CareerPath: FC = () => {
  const { taxonomyTypes } = useSelector(careerStateSelector);
  const { fetchProfileStatus, hrProfile } = useSelector(userProfileStateSelector);

  const styles = useCareerPathStyles();
  const [careerPath, setCareerPath] = React.useState<Role[]>([]);
  const [apiCallLoadingState, setApiCallLoadingState] = React.useState(LoadingStates.NOT_STARTED);
  const [userData, setUserData] = React.useState<BasicProfileData>();
  const { telemetryClient, roleLibraryService, featureService } = React.useContext(CareerHubModuleReactContext);
  const noMatchRoleId = 'NoMatchRoleId';
  const fetchData = async () => {
    setApiCallLoadingState(LoadingStates.STARTED);
    let roleId = userData?.standardTitleId || '';
    // Set RoleId to a default role value that does not exist in the role library
    // This is to simulate the experience of a user whose role is not present in the role library
    // This is for TESTING only. Should never be enabled in production.
    if (featureService.isFeatureEnabled(FeatureFlights.NoRoleContent)) {
      telemetryClient.logInfo('Role Id set to random role id for testing purposes', { originalRoleId: roleId });
      roleId = noMatchRoleId;
    }
    await roleLibraryService
      .getRoleDetails(roleId)
      .then(async (roleDetails) => {
        if (roleDetails) {
          await roleLibraryService.getCareerPath(roleId).then((data) => {
            let path = data.slice(0, 4);
            setCareerPath(path);
          });
        }
        setApiCallLoadingState(LoadingStates.SUCCEEDED);
      })
      .catch((error) => {
        if (error.status === 204) {
          telemetryClient.logEvent(FeatureName.CareerPath, SubFeatureName.NoCareerPathContent, { roleId: roleId });
          setApiCallLoadingState(LoadingStates.SUCCEEDED);
        } else {
          setApiCallLoadingState(LoadingStates.FAILED);
        }
      });
  };

  React.useEffect(() => {
    if (userData?.standardTitleId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    if (fetchProfileStatus == LoadingStates.SUCCEEDED) {
      setUserData(hrProfile);
    } else {
      setApiCallLoadingState(fetchProfileStatus);
    }
  }, [fetchProfileStatus, hrProfile]);

  const onViewAllClick: any = () => {
    telemetryClient.logButtonClick(FeatureName.CareerPath, SubFeatureName.ViewAllInCareerPath, {
      eventName: EventName.NavigateToDisciplinePage,
    });
  };

  const level2RouteType = taxonomyTypes.find((item) => item.level === 2)?.id || 'Discipline';

  return (
    <div>
      <Subtitle2 as="h2">More in your discipline</Subtitle2>
      <Link
        to={`${level2RouteType}/${userData?.disciplineId}`}
        className={styles.link}
        onClick={onViewAllClick}
        aria-label="View all roles in your discipline"
      >
        View all
      </Link>
      <SectionLoader
        announce
        errorComponent={
          <SectionError
            title={ErrorContent.sectionErrorTitle}
            description={ErrorContent.sectionErrorDescription}
            className={styles.error}
          />
        }
        state={apiCallLoadingState}
        shimmerComponent={<SkeletonCards />}
      >
        <>{careerPath.length > 0 && <CardsCarousel roleCards={careerPath} homePageStyle={true} />}</>
      </SectionLoader>
    </div>
  );
};
